/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import React, { useEffect, useMemo, useRef } from 'react';
import { IModule, IModuleConfig } from '../../contract';

export interface IPlaceHolderForContainerPreviewConfig extends IModuleConfig {
    slotId?: string;
    parent?: string;
    parentType?: string;
}

export interface IPlaceHolderForContainerPreviewResources {
    clickToConfigure: string;
}

export interface IPlaceHolderForContainerPreviewProps<T> extends IModule<T> {
    resources: IPlaceHolderForContainerPreviewResources;
    config: IPlaceHolderForContainerPreviewConfig;
}

interface IPlaceholderForContainerPreviewPropsExtended extends IPlaceHolderForContainerPreviewProps<{}> {
    id: string;
    error: Error;
    message?: string;
}

const PlaceholderForContainerPreview = (props: IPlaceholderForContainerPreviewPropsExtended): JSX.Element => {
    const ref = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (ref.current) {
            const { id, error, message, typeName } = props;
            const comment = {
                id,
                error: error && error.toString(),
                message,
                typeName,
                slotId: props.slotId,
                parentId: props.parentId
            };
            ref.current.innerHTML = `<!-- ${JSON.stringify(comment, null, 4)} -->`;
        }
    }, []);
    return useMemo(() => {
        return (
            <div
                className='card-body'
                style={{ color: '#fff', backgroundColor: '#6c757d', textAlign: 'center' }}
                role='button'
                data-ft='setting-icon'
                id={props.id}
                {...props.renderModuleAttributes(props)}
            >
                <div ref={ref} style={{ display: 'none' }} />
                <p className='card-text'>
                    <i className='ms-Icon ms-Icon--EditCreate' />
                    {props.resources.clickToConfigure}
                </p>
            </div>
        );
    }, [props]);
};

export const PlaceholderForContainerPreviewComponent = PlaceholderForContainerPreview;
